import localforage from "localforage";

const stateStore = localforage.createInstance({
  name: "GeoSahaOfflineData",
  storeName: "app_state_v3",
});

const queueStore = localforage.createInstance({
  name: "GeoSahaOfflineData",
  storeName: "sync_queue_v3",
});

const conflictStore = localforage.createInstance({
  name: "GeoSahaOfflineData",
  storeName: "sync_conflict_v3",
});

const localLocks = new Map();
let persistenceRequest = null;

function runWithLocalLock(lockName, operation) {
  const previous = localLocks.get(lockName) || Promise.resolve();
  const current = previous.catch(() => undefined).then(operation);
  localLocks.set(lockName, current);

  return current.finally(() => {
    if (localLocks.get(lockName) === current) {
      localLocks.delete(lockName);
    }
  });
}

function runWithStorageLock(lockName, operation) {
  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    return navigator.locks.request(`geo-saha:${lockName}`, operation);
  }

  return runWithLocalLock(lockName, operation);
}

function readLegacyJson(key) {
  if (!key || typeof localStorage === "undefined") return null;

  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error("Eski çevrimdışı veri okunamadı:", error);
    return null;
  }
}

async function migrateLegacyValue(store, key, legacyKey) {
  const existing = await store.getItem(key);
  if (existing != null || !legacyKey) return existing;

  const legacyValue = readLegacyJson(legacyKey);
  if (legacyValue == null) return null;

  await store.setItem(key, legacyValue);
  try {
    localStorage.removeItem(legacyKey);
  } catch (_error) {
    // IndexedDB kaydı tamamlandı; eski kopyanın kalması veri kaybı oluşturmaz.
  }
  return legacyValue;
}

export function requestPersistentOfflineStorage() {
  if (persistenceRequest) return persistenceRequest;

  persistenceRequest = (async () => {
    if (typeof navigator === "undefined" || !navigator.storage?.persist) {
      return false;
    }

    try {
      if (navigator.storage.persisted && (await navigator.storage.persisted())) {
        return true;
      }
      return await navigator.storage.persist();
    } catch (_error) {
      return false;
    }
  })();

  return persistenceRequest;
}

export async function readOfflineState(key, legacyKey = null) {
  return migrateLegacyValue(stateStore, key, legacyKey);
}

export async function writeOfflineState(key, value) {
  await runWithStorageLock(`state:${key}`, () => stateStore.setItem(key, value));
  return value;
}

export async function removeOfflineState(key) {
  await runWithStorageLock(`state:${key}`, () => stateStore.removeItem(key));
}

export async function loadQueueByKey(queueKey, legacyKey = null) {
  const value = await migrateLegacyValue(queueStore, queueKey, legacyKey);
  return Array.isArray(value) ? value : [];
}

export async function mutateQueueByKey(queueKey, mutator, legacyKey = null) {
  return runWithStorageLock(`queue:${queueKey}`, async () => {
    const current = await migrateLegacyValue(queueStore, queueKey, legacyKey);
    const normalized = Array.isArray(current) ? current : [];
    const next = await mutator(normalized);
    const nextQueue = Array.isArray(next) ? next : normalized;

    if (nextQueue.length === 0) {
      await queueStore.removeItem(queueKey);
    } else {
      await queueStore.setItem(queueKey, nextQueue);
    }

    return nextQueue;
  });
}

export async function listQueueKeys(prefix = "") {
  const keys = await queueStore.keys();
  return keys.filter((key) => !prefix || String(key).startsWith(prefix));
}

export async function loadConflictsByKey(conflictKey, legacyKey = null) {
  const value = await migrateLegacyValue(conflictStore, conflictKey, legacyKey);
  return Array.isArray(value) ? value : [];
}

export async function mutateConflictsByKey(conflictKey, mutator, legacyKey = null) {
  return runWithStorageLock(`conflict:${conflictKey}`, async () => {
    const current = await migrateLegacyValue(conflictStore, conflictKey, legacyKey);
    const normalized = Array.isArray(current) ? current : [];
    const next = await mutator(normalized);
    const nextConflicts = Array.isArray(next) ? next : normalized;

    if (nextConflicts.length === 0) {
      await conflictStore.removeItem(conflictKey);
    } else {
      await conflictStore.setItem(conflictKey, nextConflicts);
    }

    return nextConflicts;
  });
}

export async function estimateOfflineStorage() {
  if (typeof navigator === "undefined" || !navigator.storage?.estimate) {
    return null;
  }

  try {
    return await navigator.storage.estimate();
  } catch (_error) {
    return null;
  }
}
